import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import UploadProgress from "@/components/UploadProgress";
import api, { fmtErr } from "@/lib/api";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const downloadDoc = async (docId, name) => {
  try {
    const r = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(fmtErr(e));
  }
};

export function DocumentChips({ docs }) {
  if (!docs?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {docs.map((d) => (
        <button
          key={d.id}
          data-testid={`doc-download-${d.id}`}
          onClick={() => downloadDoc(d.id, d.name)}
          title={`Download ${d.name}`}
          className="flex items-center gap-2 border border-white/20 hover:border-accent hover:text-accent px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.12em] transition-colors"
        >
          <FileText size={12} /> {d.name}
        </button>
      ))}
    </div>
  );
}

export default function DocumentsManager({ hint }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/documents", fd, {
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || ev.loaded)) * 100)),
      });
      toast.success("Document uploaded.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (d) => {
    if (confirmId !== d.id) {
      setConfirmId(d.id);
      setTimeout(() => setConfirmId((c) => (c === d.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document removed.");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmId(null);
    }
  };

  return (
    <section className="mt-14" data-testid="documents-section">
      <p className="overline-label !font-semibold !text-white/85 mb-2">Documents</p>
      <p className="font-grotesk text-xs text-white/40 mb-6">{hint}</p>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={upload} className="hidden" data-testid="document-upload-input" />
      <button
        data-testid="document-upload-button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="btn-outline !px-5 !py-2.5 mb-6 flex items-center gap-2 disabled:opacity-40"
      >
        <Upload size={14} /> {uploading ? "Uploading…" : "Upload document"}
      </button>
      {uploading && progress != null && <div className="mb-6"><UploadProgress percent={progress} note="Uploading document" /></div>}
      <div className="flex flex-col">
        {(docs || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-4 border-t border-white/10 py-4" data-testid={`document-row-${d.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={16} className="text-accent shrink-0" />
              <div className="min-w-0">
                <p className="font-grotesk text-sm truncate">{d.name}</p>
                <p className="overline-label !text-[9px] mt-0.5">
                  {fmtSize(d.size)} · {new Date(d.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`document-download-${d.id}`} onClick={() => downloadDoc(d.id, d.name)} title="Download" className="border border-white/25 hover:border-accent hover:text-accent p-2 transition-colors">
                <Download size={13} />
              </button>
              <button
                data-testid={`document-delete-${d.id}`}
                onClick={() => remove(d)}
                className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors ${confirmId === d.id ? "border-accent bg-accent text-white" : "border-white/25 hover:border-accent hover:text-accent"}`}
              >
                {confirmId === d.id ? "Sure?" : <Trash2 size={13} />}
              </button>
            </div>
          </div>
        ))}
        {docs && docs.length === 0 && (
          <p className="font-display italic text-xl text-white/40 border-t border-white/10 pt-4">Nothing uploaded yet.</p>
        )}
      </div>
    </section>
  );
}
